import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import toast from "react-hot-toast";
import { fetchLedger } from "../../Apis/Wallet-api";
import WalletListFilters, {
  EMPTY_LIST_FILTERS,
} from "../../Components/Wallet/WalletListFilters";
import WalletPageShell from "../../Components/Wallet/WalletPageShell";
import WalletSelect from "../../Components/Wallet/WalletSelect";
import { formatDateTime, formatLabel, formatMoney } from "../../Utils/walletFormat";

const DEFAULT_PER_PAGE = 20;

export default function WalletLedgerPage() {
  const [walletId, setWalletId] = useState("");
  const [draftFilters, setDraftFilters] = useState(EMPTY_LIST_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_LIST_FILTERS);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [loading, setLoading] = useState(false);
  const [ledger, setLedger] = useState({ items: [], pagination: {} });

  const load = useCallback(async () => {
    if (!walletId) {
      setLedger({ items: [], pagination: {} });
      return;
    }
    setLoading(true);
    try {
      setLedger(await fetchLedger(walletId, page, perPage, appliedFilters));
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load ledger.");
    } finally {
      setLoading(false);
    }
  }, [walletId, page, perPage, appliedFilters]);

  useEffect(() => {
    load();
  }, [load]);

  const handleWalletChange = (id) => {
    setWalletId(id);
    setPage(1);
  };

  const applyFilters = () => {
    setPage(1);
    setAppliedFilters({ ...draftFilters });
  };

  const clearFilters = () => {
    setDraftFilters(EMPTY_LIST_FILTERS);
    setAppliedFilters(EMPTY_LIST_FILTERS);
    setPage(1);
  };

  const total = ledger.pagination?.total ?? 0;

  return (
    <WalletPageShell
      heading="Wallet ledger"
      subtitle="Ledger entries for a selected wallet (balance and credit trail)"
    >
      <Stack spacing={2}>
        <WalletSelect value={walletId} onChange={handleWalletChange} />
        <WalletListFilters
          variant="ledger"
          value={draftFilters}
          onChange={setDraftFilters}
          onApply={applyFilters}
          onClear={clearFilters}
        />
        {!walletId ? (
          <Typography color="text.secondary">
            Select a wallet to view ledger entries.
          </Typography>
        ) : loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <Typography variant="caption" color="text.secondary">
              {total} entr{total === 1 ? "y" : "ies"}
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Txn ID</TableCell>
                    <TableCell>Entry type</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="right">Balance after</TableCell>
                    <TableCell align="right">Credit used after</TableCell>
                    <TableCell>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(ledger.items || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Typography color="text.secondary">
                          No ledger entries match these filters.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    (ledger.items || []).map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>{row.id}</TableCell>
                        <TableCell>{row.transaction_id}</TableCell>
                        <TableCell>{formatLabel(row.entry_type)}</TableCell>
                        <TableCell align="right">
                          {formatMoney(row.amount)}
                        </TableCell>
                        <TableCell align="right">
                          {formatMoney(row.balance_after)}
                        </TableCell>
                        <TableCell align="right">
                          {formatMoney(row.credit_used_after)}
                        </TableCell>
                        <TableCell>{formatDateTime(row.created_at)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={total}
                page={Math.max(0, (ledger.pagination?.page ?? page) - 1)}
                onPageChange={(_, nextPage) => setPage(nextPage + 1)}
                rowsPerPage={ledger.pagination?.per_page ?? perPage}
                onRowsPerPageChange={(e) => {
                  setPerPage(parseInt(e.target.value, 10));
                  setPage(1);
                }}
                rowsPerPageOptions={[10, 20, 50, 100]}
              />
            </TableContainer>
          </>
        )}
      </Stack>
    </WalletPageShell>
  );
}
